import { useEffect, useState } from "react";
import { callTool } from "../api";
import { useLockPageScroll } from "../company-browser/CompanyBrowserModal";
import { ratingLineage } from "../ratings";
import { personName, type ContactActivityRow } from "./contactActivityPanes";

interface AccountHit {
  account_id: string;
  name: string | null;
  domain: string | null;
}

function Sheet({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  useLockPageScroll();
  return (
    <div
      className="company-modal-backdrop"
      role="presentation"
      onClick={onClose}
      onWheel={(event) => {
        if (event.target === event.currentTarget) event.preventDefault();
      }}
    >
      <div
        className="company-modal sheet-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="company-modal-header">
          <div className="company-modal-heading">
            <h2>{title}</h2>
            <button type="button" className="secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </header>
        <div className="company-modal-body">{children}</div>
      </div>
    </div>
  );
}

export function LinkAccountSheet({
  row,
  eventKey,
  actor,
  onClose,
  onSaved,
}: {
  row: ContactActivityRow & { company?: string | null };
  eventKey: string;
  actor: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [query, setQuery] = useState(row.company ?? "");
  const [hits, setHits] = useState<AccountHit[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function search() {
    const data = await callTool<{ results: AccountHit[] }>("account_search", {
      event_key: eventKey,
      name: query.trim() || undefined,
      limit: 20,
    });
    setHits(data.results ?? []);
  }

  useEffect(() => {
    void search().catch((err) => setError(err instanceof Error ? err.message : "account_search failed"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function attach(accountId: string) {
    setBusy(true);
    setError(null);
    try {
      await callTool("account_link_contact", {
        contact_id: row.contact_id,
        account_id: accountId,
        event_key: eventKey,
        actor,
        lineage: ratingLineage(),
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "account_link_contact failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet title={`Link account · ${personName(row)}`} onClose={onClose}>
      <form
        className="toolbar"
        onSubmit={(event) => {
          event.preventDefault();
          void search();
        }}
      >
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Account name" />
        <button type="submit">Search</button>
      </form>
      {error ? <p className="err">{error}</p> : null}
      <ul className="account-pick-list">
        {hits.map((hit) => (
          <li key={hit.account_id}>
            <button type="button" className="ghost" disabled={busy} onClick={() => void attach(hit.account_id)}>
              {hit.name ?? hit.account_id}
              {hit.domain ? ` · ${hit.domain}` : ""}
            </button>
          </li>
        ))}
      </ul>
    </Sheet>
  );
}
